import { take, call, cancelled } from 'redux-saga/effects'

import { logger } from 'utils/logger'

import {
    createParticipantConnection,
    createParticipantDisconnection,
    createDominantSpeakerChanged,
    createNetworkQualityLevelChanged,
    createParticipantTrackSubscribed,
    createParticipantTrackUnsubscribed,
    createParticipantTrackState,
} from '../channels/events/Room'
import {
    handleParticipantConnected,
    handleParticipantDisconnected,
    handleDominantSpeakerChanged,
    handleNetworkQualityLevelChanged,
    handleParticipantTrackSubscribed,
    handleParticipantTrackUnsubscribed,
    handleParticipantTrackState,
} from '../handlers/Room'

function* watchParticipantConnection(room) {
    const participantConnectionChannel = yield call(
        createParticipantConnection,
        room
    )

    try {
        while (true) {
            const { participant } = yield take(participantConnectionChannel)
            yield* handleParticipantConnected(participant, {
                roomSid: room.sid,
                localParticipantSid: room.localParticipant.sid,
            })
        }
    } catch (error) {
        logger.error(`error in handling participant connection :- ${error}`)
    } finally {
        if (yield cancelled()) {
            participantConnectionChannel.close()
        }
    }
}

function* watchParticipantDisconnection(room) {
    const participantDisconnectionChannel = yield call(
        createParticipantDisconnection,
        room
    )

    try {
        while (true) {
            const { participant } = yield take(participantDisconnectionChannel)
            yield handleParticipantDisconnected(participant)
        }
    } catch (error) {
        logger.error(`error in handling participant disconnection :- ${error}`)
    } finally {
        if (yield cancelled()) {
            participantDisconnectionChannel.close()
        }
    }
}

function* watchDominantSpeakerChanged(room) {
    const dominantSpeakerChangedChannel = yield call(
        createDominantSpeakerChanged,
        room
    )

    try {
        while (true) {
            const { participant } = yield take(dominantSpeakerChangedChannel)
            yield handleDominantSpeakerChanged(participant)
        }
    } catch (error) {
        logger.error(`error in handling dominant speaker changed :- ${error}`)
    } finally {
        if (yield cancelled()) {
            dominantSpeakerChangedChannel.close()
        }
    }
}

function* watchNetworkQualityLevelChanged(participant, isLocalUser = false) {
    const networkQualityLevelChannel = yield call(
        createNetworkQualityLevelChanged,
        participant
    )

    try {
        while (true) {
            const data = yield take(networkQualityLevelChannel)
            yield handleNetworkQualityLevelChanged(data, isLocalUser)
        }
    } catch (error) {
        logger.error(
            `error in handling network quality level changed :- ${error}`
        )
    } finally {
        if (yield cancelled()) {
            networkQualityLevelChannel.close()
        }
    }
}

function* watchParticipantTrackSubscribed(participant) {
    const participantTrackSubscribedChannel = yield call(
        createParticipantTrackSubscribed,
        participant
    )

    try {
        while (true) {
            const data = yield take(participantTrackSubscribedChannel)
            yield* handleParticipantTrackSubscribed(data)
        }
    } catch (error) {
        logger.error(
            `error in handling participant track subscribed :- ${error}`
        )
    } finally {
        if (yield cancelled()) {
            participantTrackSubscribedChannel.close()
        }
    }
}

function* watchParticipantTrackUnsubscribed(participant) {
    const participantTrackUnubscribedChannel = yield call(
        createParticipantTrackUnsubscribed,
        participant
    )

    try {
        while (true) {
            const data = yield take(participantTrackUnubscribedChannel)
            yield handleParticipantTrackUnsubscribed(data)
        }
    } catch (error) {
        logger.error(
            `error in handling participant track unsubscribed :- ${error}`
        )
    } finally {
        if (yield cancelled()) {
            participantTrackUnubscribedChannel.close()
        }
    }
}

function* watchParticipantTrackState(identity, track) {
    const participantTrackStateChannel = yield call(
        createParticipantTrackState,
        identity,
        track
    )

    try {
        while (true) {
            const data = yield take(participantTrackStateChannel)
            yield handleParticipantTrackState(data)
        }
    } catch (error) {
        logger.error(`error in handling participant track state :- ${error}`)
    } finally {
        if (yield cancelled()) {
            participantTrackStateChannel.close()
        }
    }
}

export {
    watchParticipantConnection,
    watchParticipantDisconnection,
    watchDominantSpeakerChanged,
    watchNetworkQualityLevelChanged,
    watchParticipantTrackSubscribed,
    watchParticipantTrackUnsubscribed,
    watchParticipantTrackState,
}
